import React, { useState } from 'react';
import { Mail, Loader2 } from 'lucide-react';
import { Place, EmailData } from '../../../types/results.types';
import EmailVerificationModal from '../EmailVerificationModal/EmailVerificationModal';
import { useFeatureAccess } from '../../../hooks/useFeatureAccess';
import { useAppSelector } from '../../../hooks/reduxHooks';
import { isVerifiable } from '../../../utils/emailVerifiable';
import { usePostHog } from 'posthog-js/react';

export interface VerifyModalPayload {
  personEmails: EmailData[];
  placeEmails: EmailData[];
  businessName?: string;
}

interface InlineVerifyButtonProps {
  place: Place;
  onUpgradeClick?: () => void;
  searchId?: number;
  asLink?: boolean;
  onOpenModal?: (payload: VerifyModalPayload) => void;
  label?: string;
  buttonClassName?: string;
  lockedButtonClassName?: string;
  loadingButtonClassName?: string;
}

const DEFAULT_VERIFY_BUTTON_CLASS =
  'inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-semibold transition-all duration-[120ms] ease-out text-[#8A3033] hover:bg-[#F4F4F5] active:scale-[0.98] active:bg-[#E4E4E7]';

const DEFAULT_VERIFY_LOCKED_CLASS =
  'inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-400 text-white text-xs font-semibold rounded-md hover:bg-gray-500 transition-colors';

const DEFAULT_VERIFY_LOADING_CLASS =
  'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-gray-400 bg-gray-100 cursor-not-allowed';

const InlineVerifyButton: React.FC<InlineVerifyButtonProps> = ({
  place,
  onUpgradeClick,
  searchId,
  asLink,
  onOpenModal,
  label,
  buttonClassName,
  lockedButtonClassName,
  loadingButtonClassName,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalPersonEmails, setModalPersonEmails] = useState<EmailData[]>([]);
  const [modalPlaceEmails, setModalPlaceEmails] = useState<EmailData[]>([]);
  const { hasVerificationAccess } = useFeatureAccess();
  const { activeVerification, loading } = useAppSelector((state) => state.emailVerification);
  const posthog = usePostHog();

  const isGlobalVerifying = loading || !!(activeVerification && activeVerification.status === 'processing');

  const collectAllEmails = (): { person: EmailData[]; place: EmailData[] } => {
    const person: EmailData[] = [];
    const placeEmails: EmailData[] = [];
    const seen = new Set<string>();

    (place.people || []).forEach((personRow) => {
      (personRow.emails || []).forEach((email) => {
        if (!seen.has(email.email) && email.id) {
          person.push(email);
          seen.add(email.email);
        }
      });
    });
    (place.company_emails || []).forEach((email) => {
      if (!seen.has(email.email) && email.id) {
        placeEmails.push(email);
        seen.add(email.email);
      }
    });
    return { person, place: placeEmails };
  };

  const allEmails = collectAllEmails();
  const verifiablePersonEmails = allEmails.person.filter(isVerifiable);
  const verifiablePlaceEmails = allEmails.place.filter(isVerifiable);
  const allVerifiable = [...verifiablePersonEmails, ...verifiablePlaceEmails];

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalPersonEmails([]);
    setModalPlaceEmails([]);
  };

  const openModal = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (isGlobalVerifying) return;
    posthog?.capture('verify_emails_clicked', {
      context: asLink ? 'row_inline_link' : 'row',
      search_id: searchId,
      email_count: allVerifiable.length,
    });
    if (onOpenModal) {
      onOpenModal({ personEmails: verifiablePersonEmails, placeEmails: verifiablePlaceEmails, businessName: place.name });
      return;
    }
    setModalPersonEmails(verifiablePersonEmails);
    setModalPlaceEmails(verifiablePlaceEmails);
    setIsModalOpen(true);
  };

  // In standalone (uncontrolled) mode only — parent owns the modal when onOpenModal is provided.
  const standaloneModal = !onOpenModal && isModalOpen ? (
    <EmailVerificationModal
      isOpen
      onClose={handleCloseModal}
      personEmails={modalPersonEmails}
      placeEmails={modalPlaceEmails}
      emailType="both"
      searchId={searchId}
      bulkMode={false}
      businessName={place.name}
      skipInitialSelection
    />
  ) : null;

  if (allVerifiable.length === 0 && !isModalOpen) {
    return null;
  }

  if (!hasVerificationAccess) {
    if (asLink) {
      return (
        <button
          onClick={(event) => { event.stopPropagation(); onUpgradeClick?.(); }}
          className="text-[11px] text-[#854F0B] font-medium hover:underline bg-transparent border-0 p-0 cursor-pointer"
        >
          Verify ({allVerifiable.length})
        </button>
      );
    }
    return (
      <button
        onClick={(event) => { event.stopPropagation(); onUpgradeClick?.(); }}
        className={lockedButtonClassName ?? DEFAULT_VERIFY_LOCKED_CLASS}
        title="Upgrade to Growth plan to verify emails"
      >
        {label ?? 'Verify remaining emails'}
      </button>
    );
  }

  const verifyLabel = asLink
    ? label ?? `Verify (${allVerifiable.length})`
    : label ?? 'Verify remaining emails';

  const resolvedButtonClassName = (() => {
    if (asLink) {
      return `text-[11px] font-medium bg-transparent border-0 p-0 cursor-pointer transition-colors ${
        isGlobalVerifying ? 'text-gray-400 cursor-not-allowed' : 'text-[#854F0B] hover:underline'
      }`;
    }
    if (isGlobalVerifying) {
      return loadingButtonClassName ?? DEFAULT_VERIFY_LOADING_CLASS;
    }
    return buttonClassName ?? DEFAULT_VERIFY_BUTTON_CLASS;
  })();

  return (
    <>
      {allVerifiable.length > 0 && (
        <button
          onClick={openModal}
          disabled={isGlobalVerifying}
          className={resolvedButtonClassName}
        >
          {isGlobalVerifying ? (
            <span className="inline-flex items-center gap-1">
              <Loader2 className={`${asLink ? 'w-2.5 h-2.5' : 'w-3 h-3'} animate-spin`} />
              Verifying…
            </span>
          ) : (
            <>
              {!asLink && <Mail className="w-3 h-3" />}
              {verifyLabel}
            </>
          )}
        </button>
      )}
      {standaloneModal}
    </>
  );
};

export default InlineVerifyButton;
